import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, ChevronDown, Plus, MapPin, Check, X, Loader2 } from 'lucide-react';
import { getApiUrl } from '../utils/api';

const API = getApiUrl();

/**
 * V5.99 — Multi-sites switcher.
 *
 * Displays in the sidebar only when the tenant has more than 1 site, OR when the
 * current user can manage sites (owner/patron) — so they can add their first
 * additional site from anywhere.
 *
 * Behavior:
 *  - GET /api/tenant/sites → load sites + active_site_id
 *  - POST /api/tenant/sites/switch → set active site cookie (1 year)
 *  - POST /api/tenant/sites → create new site (owner only)
 *  - Reloads the page after a switch so all data refetches with the new context.
 */
export const SiteSwitcher = ({ tenant, tenantName, meta, primaryColor = '#F0592B', open: controlledOpen, onOpenChange } = {}) => {
  const [sites, setSites] = useState([]);
  const [activeSiteId, setActiveSiteId] = useState('main');
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = (v) => {
    const next = typeof v === 'function' ? v(open) : v;
    if (onOpenChange) onOpenChange(next); else setInternalOpen(next);
  };
  const [showAdd, setShowAdd] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [newName, setNewName] = useState('');
  const [newAddress, setNewAddress] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/tenant/sites`, { withCredentials: true });
      setSites(r.data?.sites || []);
      setActiveSiteId(r.data?.active_site_id || 'main');
      setCanManage(!!r.data?.can_manage);
    } catch {
      // silent — sidebar shouldn't be noisy
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Fermeture au clavier (Échap) quand le menu est ouvert
  useEffect(() => {
    if (!open) return undefined;
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [open]);

  const handleSwitch = async (siteId) => {
    if (siteId === activeSiteId) { setOpen(false); return; }
    try {
      await axios.post(
        `${API}/tenant/sites/switch`,
        { site_id: siteId },
        { withCredentials: true }
      );
      toast.success(`Site changé · ${sites.find((s) => s.site_id === siteId)?.name || siteId}`);
      // Reload the page so every data view refetches with the new site cookie
      setTimeout(() => window.location.reload(), 350);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur changement de site');
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) {
      toast.error('Le nom du site est requis');
      return;
    }
    setCreating(true);
    try {
      const r = await axios.post(
        `${API}/tenant/sites`,
        { name: newName.trim(), address: newAddress.trim() },
        { withCredentials: true }
      );
      setSites(r.data?.sites || []);
      toast.success(`Site « ${newName} » créé`);
      setNewName('');
      setNewAddress('');
      setShowAdd(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur création du site');
    } finally {
      setCreating(false);
    }
  };

  // Pays · Devise d'un établissement (consigne 01.1) — pays affiché en toutes lettres
  const siteMeta = (s) => {
    let pays = '';
    try { if (s?.country) pays = new Intl.DisplayNames(['fr'], { type: 'region' }).of(s.country); } catch { pays = s?.country || ''; }
    return [pays, s?.currency].filter(Boolean).join(' · ');
  };

  // Don't render if loading or if user has no sites context at all
  if (loading) return null;

  const activeSite = sites.find((s) => s.site_id === activeSiteId) || sites[0] || {};
  const single = sites.length <= 1;
  const mainId = (sites.find((x) => x.site_id === 'main') || sites[0] || {}).site_id;

  // Logo réel de l'établissement (branding) — jamais l'emoji avocat
  const logoUrl = tenant?.branding?.logo_type === 'avocado' ? null : (tenant?.branding?.logo_url || tenant?.logo_url || null);

  // Vignette d'établissement (maquette) : vrai logo dans un cadre blanc pour le principal, initiales colorées pour les autres
  const INITIAL_COLORS = ['#7C6FF0', '#2FA36B', '#E0733A', '#3B82C4', '#C44D8F', '#D9A441'];
  const initialsOf = (name = '') => name.trim().split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase() || '•';
  const SiteAvatar = ({ s, main, size = 32 }) => {
    if (main) return (
      <span className="rounded-xl flex items-center justify-center shrink-0 overflow-hidden bg-[var(--carte)] border border-black/[0.08]" style={{ width: size, height: size }}>
        {logoUrl && !logoError ? <img src={logoUrl} alt={tenantName || s?.name || ''} className="w-full h-full object-contain p-0.5" onError={() => setLogoError(true)} /> : <Building2 size={Math.round(size * 0.52)} className="text-[var(--texte-2)]" />}
      </span>
    );
    const nm = s?.name || '';
    const color = INITIAL_COLORS[[...nm].reduce((a, c) => a + c.charCodeAt(0), 0) % INITIAL_COLORS.length];
    return (
      <span className="rounded-full flex items-center justify-center shrink-0 text-[var(--sur-couleur)] font-bold" style={{ width: size, height: size, background: color, fontSize: Math.round(size * 0.4) }}>
        {initialsOf(nm)}
      </span>
    );
  };

  return (
    <div className="relative" data-testid="site-switcher">
      {/* 01.1 · Ligne 1 = établissement : logo, nom, pays · devise, chevron → menu « Mes établissements » */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        title="Mes établissements"
        className="w-full flex items-center gap-2.5 text-left rounded-2xl bg-[var(--carte)] border border-black/[0.06] shadow-[0_1px_4px_rgba(0,0,0,0.05)] hover:shadow-[0_3px_12px_rgba(0,0,0,0.09)] transition-shadow pl-2.5 pr-[52px] py-2"
        data-testid="site-switcher-trigger"
      >
        <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 overflow-hidden bg-[var(--carte)] border border-black/[0.08]">
          {logoUrl && !logoError ? <img src={logoUrl} alt={tenantName || activeSite.name || ''} className="w-full h-full object-contain p-0.5" data-testid="site-switcher-logo" onError={() => setLogoError(true)} /> : <Building2 size={17} className="text-[var(--texte-2)]" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-[15px] text-[var(--texte)] truncate leading-tight" data-testid="site-switcher-name">{tenantName || activeSite.name || 'Mon établissement'}</p>
          {meta && <p className="text-xs text-[var(--texte-2)] truncate leading-tight" data-testid="site-switcher-meta">{meta}</p>}
        </div>
        <ChevronDown size={16} className={`text-[var(--texte-2)] transition-transform shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Menu « Mes établissements » */}
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} data-testid="site-switcher-backdrop" aria-hidden="true" />
          <div
          className="absolute z-50 left-0 right-0 top-full mt-1 bg-[var(--carte)] border border-[var(--bord)] rounded-xl shadow-xl py-1.5 max-h-[420px] overflow-y-auto"
          data-testid="site-switcher-dropdown"
        >
          <p className="px-3 pt-1 pb-1.5 text-xs uppercase tracking-wider font-semibold text-[var(--texte-2)]">Mes établissements</p>
          {single && (
            <div className="px-3 py-2 flex items-center gap-2.5" data-testid="site-current">
              <SiteAvatar s={activeSite} main />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-[var(--texte)] truncate">{tenantName || activeSite.name || 'Mon établissement'}</p>
                {siteMeta(activeSite) && <p className="text-xs text-[var(--texte-2)] truncate leading-tight" data-testid="site-current-meta">{siteMeta(activeSite)}</p>}
              </div>
              <Check size={14} className="text-[var(--brand)] shrink-0" />
            </div>
          )}
          {!single && sites.map((s) => {
            const isActive = s.site_id === activeSiteId;
            return (
              <button
                key={s.site_id}
                onClick={() => handleSwitch(s.site_id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-[var(--remplissage)] transition-colors text-left ${
                  isActive ? 'bg-[var(--brand-soft)]/60' : ''
                }`}
                data-testid={`site-option-${s.site_id}`}
              >
                <SiteAvatar s={s} main={s.site_id === mainId} />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-bold leading-tight truncate ${isActive ? 'text-[var(--brand)]' : 'text-[var(--texte)]'}`}>
                    {s.name}
                  </p>
                  {siteMeta(s) && <p className="text-xs font-medium text-[var(--texte-2)] truncate leading-tight mt-0.5" data-testid={`site-option-meta-${s.site_id}`}>{siteMeta(s)}</p>}
                  {s.address && (
                    <p className="text-xs text-[var(--texte-2)] truncate mt-0.5 leading-tight">{s.address}</p>
                  )}
                </div>
                {isActive && <Check size={14} className="text-[var(--brand)] shrink-0" />}
              </button>
            );
          })}

          {canManage && (
            <>
              {!single && <div className="my-1 mx-3 border-t border-[var(--bord)]" />}
              {!showAdd ? (
                <button
                  onClick={() => setShowAdd(true)}
                  className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-[var(--brand-soft)] transition-colors text-left text-[var(--brand)] font-semibold text-sm"
                  data-testid="site-add-btn"
                >
                  <Plus size={15} /> Ajouter un établissement
                </button>
              ) : (
                <div className="px-3 py-2 space-y-2" data-testid="site-add-form">
                  <input
                    type="text"
                    placeholder="Nom du resto (ex: Zebra Casablanca)"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className="w-full px-2.5 py-2 bg-[var(--remplissage)] border border-[var(--bord)] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    data-testid="site-new-name"
                    autoFocus
                  />
                  <input
                    type="text"
                    placeholder="Adresse (optionnel)"
                    value={newAddress}
                    onChange={(e) => setNewAddress(e.target.value)}
                    className="w-full px-2.5 py-2 bg-[var(--remplissage)] border border-[var(--bord)] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    data-testid="site-new-address"
                  />
                  <div className="flex gap-1.5">
                    <button
                      onClick={handleCreate}
                      disabled={creating || !newName.trim()}
                      className="flex-1 py-1.5 bg-[var(--brand)] hover:bg-[var(--brand-hover)] disabled:opacity-40 text-[var(--sur-couleur)] rounded-lg text-xs font-bold flex items-center justify-center gap-1.5"
                      data-testid="site-create-confirm"
                    >
                      {creating ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                      Créer
                    </button>
                    <button
                      onClick={() => { setShowAdd(false); setNewName(''); setNewAddress(''); }}
                      className="px-2.5 py-1.5 bg-[var(--remplissage)] hover:bg-[var(--remplissage)] text-[var(--texte)] rounded-lg text-xs font-bold flex items-center"
                      data-testid="site-create-cancel"
                    >
                      <X size={12} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        </>
      )}
    </div>
  );
};

export default SiteSwitcher;
